/**
 * Query building and client-side ordering/filtering for the nav bar's project search.
 *
 * Design: to avoid a Meilisearch backfill of date fields onto existing records, the date-based
 * SORT and the due-date FILTER run CLIENT-SIDE on the hydrated Firestore documents, which every
 * project already has (dateCreated as epoch ms, dueDate as a YYYY-MM-DD string). Meilisearch does
 * only the text query, the STATUS filter (status is already indexed on every record, so it needs
 * no backfill), and the result limit. Nothing new has to be indexed or backfilled.
 *
 * Tradeoff: client-side sort/filter operate on the returned page (top `limit` by relevance), and
 * results reorder as their Firestore docs hydrate. For the identifier-style searches this bar is
 * mostly used for (a model name, a PO, a client) the whole result set fits in the page, so this is
 * fine; for very broad queries it orders the returned page rather than the global set.
 *
 * These helpers live outside Navigation.vue so they can be unit tested: the component's template
 * uses optional chaining that the Vue 2 template compiler cannot parse under jest.
 */

const DAY_MS = 86400000

// Meilisearch defaults to 20; this is the returned page that client-side sort/filter work over.
export const PROJECT_SEARCH_LIMIT = 50

export const DUE_DATE_FILTER_OPTIONS = [
  { text: 'Any', value: 'any' },
  { text: 'Overdue', value: 'overdue' },
  { text: 'Next 7 days', value: 'week' },
  { text: 'No due date', value: 'none' },
]

// Each sort mode names the hydrated project field it orders by. 'relevance' keeps Meilisearch's own
// order. dateCreated is epoch ms (numeric); dueDate is a YYYY-MM-DD string that sorts chronologically
// as a plain string. The `<`/`>` comparator below handles both.
export const PROJECT_SORT_OPTIONS = [
  { text: 'Newest first', value: 'newest', field: 'dateCreated', direction: 'desc' },
  { text: 'Oldest first', value: 'oldest', field: 'dateCreated', direction: 'asc' },
  { text: 'Best match', value: 'relevance', field: null, direction: null },
  { text: 'Due date', value: 'dueSoonest', field: 'dueDate', direction: 'asc' },
]

// Best match (Meilisearch relevance) is the default sort. The filter/sort controls reset to their
// defaults (all statuses, any due date, best match) each time the search closes, so a selection
// never persists into the next search (see the showResults watcher in Navigation.vue).
export const DEFAULT_PROJECT_SORT = 'relevance'

/**
 * Meilisearch options. The text query is passed separately; here we build only the SERVER-SIDE
 * filter (status, which is already indexed on every record) and the limit. Date sorting and the
 * due-date filter are applied client-side afterwards, see orderAndFilterProjectResults.
 *
 * @param {object} args
 * @param {Array<number>} args.statusFilter Selected ProjectStatus values, empty for all.
 * @returns {{ filter: string|undefined, limit: number }}
 */
export const buildProjectSearchOptions = ({ statusFilter = [] } = {}) => {
  const filters = []
  if (statusFilter.length) {
    filters.push(`(${statusFilter.map(s => `status = ${s}`).join(' OR ')})`)
  }
  return {
    filter: filters.join(' AND ') || undefined,
    limit: PROJECT_SEARCH_LIMIT,
  }
}

// Local calendar day (YYYY-MM-DD) for `ms`. dueDate is a bare calendar day, so we compare against
// the viewer's local day, which is what "overdue" / "next 7 days" mean to a person.
const toLocalYMD = (ms) => {
  const d = new Date(ms)
  const p = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

const isMissing = (v) => v === undefined || v === null || v === ''

/**
 * Applies the due-date filter and the chosen sort to the (possibly still hydrating) result items.
 *
 * Items are hydrated Firestore project docs (with dateCreated / dueDate) or, until hydration lands,
 * the raw uuid-only search hit. Missing values sort last and are excluded by the date-range filters,
 * so an un-hydrated item simply settles into place once its Firestore doc arrives.
 *
 * @param {Array<object>} items Hydrated project docs (or raw hits), in Meilisearch relevance order.
 * @param {object} opts
 * @param {string} opts.sortMode One of PROJECT_SORT_OPTIONS' values.
 * @param {string} opts.dueDateFilter One of DUE_DATE_FILTER_OPTIONS' values.
 * @param {number} opts.now Epoch ms (injectable for tests).
 * @returns {Array<object>} The items, filtered then ordered.
 */
export const orderAndFilterProjectResults = (
  items,
  { sortMode = DEFAULT_PROJECT_SORT, dueDateFilter = 'any', now = Date.now() } = {}
) => {
  let result = items

  if (dueDateFilter !== 'any') {
    const today = toLocalYMD(now)
    const weekEnd = toLocalYMD(now + 7 * DAY_MS)
    result = result.filter((p) => {
      const due = p && p.dueDate
      if (dueDateFilter === 'none') return !due
      if (!due) return false
      if (dueDateFilter === 'overdue') return due < today
      if (dueDateFilter === 'week') return due >= today && due <= weekEnd
      return true
    })
  }

  const mode = PROJECT_SORT_OPTIONS.find((o) => o.value === sortMode)
  if (!mode || !mode.field) return result

  const { field, direction } = mode
  const sign = direction === 'asc' ? 1 : -1
  return [...result].sort((a, b) => {
    const av = a ? a[field] : undefined
    const bv = b ? b[field] : undefined
    if (isMissing(av) && isMissing(bv)) return 0
    if (isMissing(av)) return 1
    if (isMissing(bv)) return -1
    if (av < bv) return -1 * sign
    if (av > bv) return 1 * sign
    return 0
  })
}
